import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import DetailSheet from '../components/evangile/DetailSheet';import { EvangileItem } from '../hooks/useEvangile';
import { incrementEvangileView } from '../services/evangileViews';

const formatDate = (dateValue: string, locale: string, withYear: boolean) => {
  if (!dateValue) return '';
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  const options: Intl.DateTimeFormatOptions = withYear
    ? { day: '2-digit', month: 'long', year: 'numeric' }
    : { day: '2-digit', month: 'short' };
  return new Intl.DateTimeFormat(locale, options).format(date);
};

const normalizeDate = (d: string) => {
  if (/^\d{8}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
  return d;
};

const EvangileDetail: React.FC = () => {
  const { lang, date: rawDate } = useParams();
  const date = rawDate ? normalizeDate(rawDate) : rawDate;
  const locale = lang || 'fr';
  console.log('[EvangileDetail] render — lang:', lang, 'rawDate:', rawDate, 'date:', date);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<EvangileItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [viewsBoost, setViewsBoost] = useState(0);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setError(false);
    setViewsBoost(0);
    console.log('[EvangileDetail] fetching Firestore — collection:', `evangile_${locale}`, 'doc:', date);
    const docRef = doc(collection(db, `evangile_${locale}`), date);
    getDoc(docRef)
      .then(snap => {
        console.log('[EvangileDetail] Firestore result — exists:', snap.exists(), 'id:', snap.id);
        if (!snap.exists()) {
          setError(true);
          setLoading(false);
          return;
        }
        const data = snap.data();
        setItem({
          id: snap.id,
          date: data.date || snap.id,
          reference: data.reference || '',
          accroche: data.accroche || '',
          contenu: data.contenu || '',
          caption: data.caption || null,
          imageUrl: data.image_url || '',
          thumbUrl: data.image_thumb_url || data.image_url || '',
          imagePortraitUrl: data.image_portrait_url || null,
          portraitThumbUrl: data.image_portrait_thumb_url || null,
          videoUrl: data.video_url || null,
          lectureAudioUrl: data.lecture_audio_url || null,
          views: typeof data.views === 'number' ? data.views : 0,
        });
        setLoading(false);
        // Compte une lecture (dédupliquée par navigateur). Affichage optimiste +1.
        incrementEvangileView(locale, date).then(counted => {
          if (counted) setViewsBoost(1);
        });
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [date, locale]);

  const canonicalUrl = `https://bible4.me/${locale}/evangile/${date}`;

  // Préparer les balises OG si l'item est chargé
  const ogTitle = item ? `${item.reference} — Bible4Me` : t('evangile.titre');
  const ogDescription = item ? `${item.reference} — ${item.accroche}` : '';
  const ogImage = item ? item.imageUrl : '';
  const ogUrl = canonicalUrl;

  return (
    <>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:url" content={ogUrl} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="min-h-screen bg-amber-100 text-amber-900">
        <div className="sticky top-0 z-20 bg-amber-200 px-4 py-3 shadow-sm">
          <button
            type="button"
            aria-label={t('common.goBack')}
            onClick={() => navigate(`/${locale}/evangile`)}
            className="text-amber-900 font-bold text-sm hover:opacity-70 transition-opacity"
          >
            ← {t('common.goBack')}
          </button>
        </div>
        <div className="px-4 py-8">
          {loading && <div className="text-center text-sm text-amber-700">{t('common.loading')}</div>}
          {error && <div className="text-center text-sm text-amber-700">{t('evangile.notFound')}</div>}
          {item && (
            <>
              <DetailSheet
                evangile={item}
                locale={locale}
                onClose={() => navigate(`/${locale}/evangile`)}
                dateLabel={formatDate(item.date, locale, true)}
                openLabel={t('evangile.ouvrir')}
                shareLabel={t('evangile.partager')}
                nativeShareLabel={t('evangile.nativeShare')}
                listenLabel={t('evangile.ecouter')}
                pauseLabel={t('evangile.ecouterPause')}
              />
              <div className="mt-4 flex items-center justify-center gap-1 text-amber-700 text-sm">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span>{t('evangile.reads', { count: item.views + viewsBoost })}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EvangileDetail;
